import {
  Box,
  Text,
  Input,
  Button,
  RadioGroup,
  Radio,
  Flex,
  Divider
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormFeedback, useToast } from '@morressier/alembic';
import {
  apiServerInfo,
  exportConfigItem,
  exportServerInfo,
  gcsServerInfo,
  s3ServerInfo
} from '@publishing-engine/domain/models';
import { IExportFormValues } from '@publishing-engine/domain/models/vendor-exports';
import { useForm } from 'react-hook-form';
import { useRetrySubmissionExport } from 'src/api/mutations';
import { testExportConnections } from 'src/api/queries';
import { exportFormSchema } from '@publishing-engine/domain/validation/vendorExportRetryFormValidation';
import { FieldLabel } from 'src/components/Field';
import VendorExportServerInfo from 'src/components/VendorExport/VendorExportServerInfo';
interface IProps {
  item: exportConfigItem;
  submissionId: string;
  submissionContextId: string;
  setIsOpen: (open: boolean) => void;
  setEditRetryLoadingMap: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  organizationId: string;
}

const EditAndRetry: React.FC<IProps> = ({
  item,
  submissionId,
  submissionContextId,
  setIsOpen,
  setEditRetryLoadingMap,
  organizationId
}) => {
  const methods = useForm<IExportFormValues>({
    resolver: zodResolver(exportFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      exportName: item?.name ?? '',
      exportType: item?.exportType ?? 'full',
      configureType: item?.exportConfigType
    }
  });
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    trigger,
    formState: { isSubmitting, errors }
  } = methods;
  const toast = useToast();
  const { retrySubmissionExportAsync } = useRetrySubmissionExport(submissionId);
  const exportType = watch('exportType');
  const configureType = watch('configureType');

  const onSubmit = async (values: IExportFormValues) => {
    try {
      let serverInfo:
        | exportServerInfo
        | s3ServerInfo
        | gcsServerInfo
        | apiServerInfo;

      if (values.configureType === 's3') {
        serverInfo = {
          bucket: values.serverInfo.bucket!,
          region: values.serverInfo.region!,
          access_key: values.serverInfo.access_key!,
          secret_key: values.serverInfo.secret_key!,
          path: values.serverInfo.path ?? ''
        };
      } else if (values.configureType === 'gcs') {
        serverInfo = {
          bucket: values.serverInfo.bucket!,
          project_id: values.serverInfo.project_id!,
          private_key: values.serverInfo.private_key!,
          client_email: values.serverInfo.client_email!,
          path: values.serverInfo.path!
        };
      } else {
        serverInfo = {
          host: values.serverInfo.host!,
          port: Number(values.serverInfo.port!),
          username: values.serverInfo.username!,
          password: values.serverInfo.password!,
          path: values.serverInfo.path ?? ''
        };
      }
      const isConnectionSuccessful = await testConnection(values);
      if (!isConnectionSuccessful) return;

      toast({
        variant: 'attention',
        title: `Retry Initiated`,
        description: 'Please wait for a while. Export is in progress'
      });

      const exportConfig = {
        id: item.id,
        name: values.exportName,
        exportType: values.exportType,
        exportConfigType: values.configureType,
        serverInfo
      };

      const payload = [
        {
          exportConfig,
          exportConfigId: item.id,
          submissions: [{ submissionId, submissionContextId, organizationId }]
        }
      ];

      const response = await retrySubmissionExportAsync(payload);

      if (response.isSuccess) {
        toast({
          variant: 'success',
          title: 'Export successful',
          description: 'Submission exported successfully'
        });
        setIsOpen(false);
      } else {
        toast({
          variant: 'error',
          title: 'Export failed',
          description: 'Export failed. Please try again later'
        });
      }
    } catch {
      toast({
        variant: 'error',
        title: 'Retry failed'
      });
    } finally {
      setEditRetryLoadingMap(p => ({ ...p, [item.id]: false }));
    }
  };

  const testConnection = async (
    values: IExportFormValues
  ): Promise<boolean | string> => {
    const isValid = await trigger('serverInfo');
    if (!isValid) {
      return false;
    }
    const serverInfo = values.serverInfo;
    try {
      const response = await testExportConnections(
        item.exportConfigType,
        JSON.stringify(serverInfo)
      );

      toast({
        variant: response.success ? 'success' : 'error',
        title: response.success ? 'Connection successful' : 'Connection failed',
        description: response.success
          ? 'Server details verified'
          : 'Please check your server details'
      });

      return response.success;
    } catch {
      toast({
        variant: 'error',
        title: 'Connection failed',
        description: 'Please verify server details before retrying.'
      });

      return false;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Box bg="white" p={{ base: 3, md: 6 }} borderRadius="md">
        <Text fontSize="lg" fontWeight="semibold">
          Export to a new location
        </Text>
        <Text fontSize="sm" mb={6}>
          Enter the server details for a temporary export
        </Text>
        <Divider />
        <Box mt={4}>
          <Flex direction="column" gap={2}>
            <FieldLabel subText="" label="Export Name" isRequired />
            <Input {...register('exportName')} isDisabled />
            {errors.exportName && (
              <FormFeedback variant="error">
                {errors.exportName.message}
              </FormFeedback>
            )}
            <FieldLabel subText="" label="Select Export Type" isRequired />
            <RadioGroup
              value={exportType}
              onChange={value =>
                setValue('exportType', value as 'full' | 'metadata')
              }
              isDisabled
            >
              <Flex gap={2}>
                <Radio value="full">
                  Package file (.zip file, pdf, images)
                </Radio>
                <Radio value="basic">Metadata only (.xml file)</Radio>
              </Flex>
            </RadioGroup>
            {errors.exportType && (
              <FormFeedback variant="error">
                {errors.exportType.message}
              </FormFeedback>
            )}
            <FieldLabel subText="" label="Select Configure Type" isRequired />
            <RadioGroup
              value={configureType}
              onChange={value =>
                setValue(
                  'configureType',
                  value as 'ftp' | 's3' | 'sftp' | 'gcs'
                )
              }
              isDisabled
            >
              <Flex gap={2} flexWrap="wrap">
                <Radio value="sftp">SFTP</Radio>
                <Radio value="ftp">FTP</Radio>
                <Radio value="s3">S3 bucket</Radio>
                <Radio value="gcs">GCS bucket</Radio>
              </Flex>
            </RadioGroup>
            {errors.configureType && (
              <FormFeedback variant="error">
                {errors.configureType.message}
              </FormFeedback>
            )}
          </Flex>
          {configureType && (
            <VendorExportServerInfo
              type={configureType}
              isSubmitting={isSubmitting}
              errors={errors}
              testConnection={testConnection}
              getValues={getValues}
              register={register}
              setValue={setValue}
            />
          )}
          <Flex justifyContent="flex-end" gap={3}>
            <Button
              variant={'secondary'}
              onClick={() => {
                setIsOpen(false);
                setEditRetryLoadingMap(p => ({ ...p, [item.id]: false }));
              }}
            >
              Cancel
            </Button>
            <Button type="submit" variant={'primary'} isLoading={isSubmitting}>
              Export
            </Button>
          </Flex>
        </Box>
      </Box>
    </form>
  );
};

export default EditAndRetry;
